// src/store/useMetadataProgressStore.ts
//
// ORGANIZATION SCOPING
// --------------------
// `currentRetrievalId` / `selectedRetrievalId` name retrieval jobs belonging to
// ONE organization, and `isFetching` / `progress` describe that org's fetch.
// Persisting them under a single localStorage key meant Org B loaded showing a
// progress bar for Org A's retrieval, pointing at a retrieval id Org B cannot
// resolve. The document is now namespaced per organization, and the in-memory
// copy is re-read on every switch.
import { create } from "zustand";
import { getQueryClient } from "@/dataProvider";
import { persist } from "zustand/middleware";
import {
  createOrgScopedJSONStorage,
  dropLegacyGlobalPersistedState,
  registerOrgScopedState,
} from "@/lib/org-scope";
import { apollo } from "@/lib/apollo";
import { START_METADATA_RETRIEVAL } from "@/gql/metadataRetrival";
import { getMetadataWsClient } from "@/gql/metadataWsClient";
import { Socket, Channel } from "phoenix"; 
import env from "@/config/env";

type MetadataProgressValues = {
    isFetching: boolean;
    progress: number;
    message: string;
    target: string | null;
    modalClosedByUser: boolean;
    retrievalCompleted: boolean;
    subscriptionObserver: { unsubscribe: () => void } | null;
    currentRetrievalId: string | null;
    selectedRetrievalId: string | null;
  };

interface MetadataProgressState extends MetadataProgressValues {
  startRetrieval: (opts: {
    token: string;
    orgId: string;
    target?: string;
  }) => Promise<void>;

  cleanup: () => void;
  setMessage: (msg: string) => void;
  setProgress: (percentage: number) => void;
  setModalClosedByUser: (val: boolean) => void;
  setRetrievalCompleted: (val: boolean) => void;
  setSelectedRetrievalId: (id: string | null) => void;
}

const query = getQueryClient()

const PERSIST_NAME = "metadata-progress-storage";

// The un-namespaced document written before this store was org-scoped.
dropLegacyGlobalPersistedState(PERSIST_NAME);

export const useMetadataProgressStore = create<MetadataProgressState>()(
    persist((set, get) => ({
  isFetching: false,
  modalClosedByUser: false,
  retrievalCompleted: false,
  progress: 0,
  message: "",
  target: null,
  subscriptionObserver: null,
  currentRetrievalId: null,
  selectedRetrievalId: null,

  setMessage: (msg) => set({ message: msg }),
  setProgress: (percentage) => set({ progress: percentage }),
  setModalClosedByUser: (val) => set({ modalClosedByUser: val }),
  setRetrievalCompleted: (val) => set({ retrievalCompleted: val }),
  setSelectedRetrievalId: (id) => set({ selectedRetrievalId: id }),
  cleanup: () => {
    const { subscriptionObserver } = get();
    if (subscriptionObserver) {
      subscriptionObserver.unsubscribe();
    }
    // Note: We don't dispose the WebSocket client here because it might be used by other subscriptions
    // The client will auto-cleanup when the connection closes
    set({
      subscriptionObserver: null,
      isFetching: false,
      progress: 0,
      message: "",
      target: null,
    });
  },

  startRetrieval: async ({ token, orgId, target }) => {
    const { subscriptionObserver, cleanup, setMessage, setProgress, setRetrievalCompleted } = get();

    if (get().isFetching) return;

    // Cleanup any existing subscription
    if (subscriptionObserver) {
      subscriptionObserver.unsubscribe();
    }

    set({
      isFetching: true,
      progress: 5,
      message: "Starting metadata retrieval...",
      target: target || null,
      retrievalCompleted: false,
    });

    try {
      // Start the metadata retrieval mutation
      const result = await apollo.mutate({
        mutation: START_METADATA_RETRIEVAL,
        variables: {
          input: {
            organizationId: orgId,
          }
        },
        context: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      });

      if (!result.data) {
        throw new Error("Failed to start metadata retrieval");
      }

      const retrievalId = (result.data as any).startMetadataRetrieval?.id;
      if (retrievalId) {
        set({ currentRetrievalId: retrievalId, selectedRetrievalId: retrievalId });
      }

      setProgress(10);
      setMessage("Connected to progress updates...");

      // 2. Phoenix Channel subscription (NEW)
      const socket = new Socket(`${env.WS_BASE_URL}`, {
        params: { token }, // JWT auth
      });

      socket.connect();

      const topic = `progress:retrieval:${retrievalId}`;
      const channel = socket.channel(topic, {});

      channel
        .join()
        .receive("ok", () => {})
        .receive("error", () => {
          setMessage("Failed to connect to progress updates");
        });

      channel.on("progress_update", (payload: any) => {
        switch (payload.status) {
          case "in_progress":
            setProgress(payload.progress_percentage ?? 0);
            setMessage(
              payload.current_phase
                ? `${payload.current_phase}: ${payload.processed_items}/${payload.total_items}`
                : `Processing... ${payload.progress_percentage}%`
            );
            break;
          case "completed":
            setProgress(100);
            setRetrievalCompleted(true);
            setMessage(payload.phase_details?.message || "Metadata retrieval completed ✅");
            // Your existing completion logic
            setTimeout(() => {
              set({ isFetching: false, progress: 0, message: "", target: null, retrievalCompleted: false, subscriptionObserver: null });
              unsubscribe();
              query.invalidateQueries({ queryKey: ["metadataItems", orgId] });
            }, 2000);
            break;
          case "failed":
            setMessage(`❌ ${payload.phase_details?.message || "Retrieval failed"}`);
            set({ isFetching: false, subscriptionObserver: null });
            unsubscribe();
            break;
        }

      });

      channel.on("phase_transition", (payload: any) => {
        setProgress(payload.progress_percentage ?? 0);
        setMessage(`Now ${payload.to_phase}...`);
      });

      channel.on("retrieval_error", (payload: any) => {
        setMessage(`Retrieval error: ${payload.error_message}`);
      });

      // Store unsubscribe function
      const unsubscribe = () => {
        channel.leave();
        socket.disconnect();
      };

      set({ subscriptionObserver: { unsubscribe } });

      
    } catch (err) {
      setMessage("Retrieval failed");
      set({ isFetching: false });
      cleanup();
    }
  },

}),
{
    name: PERSIST_NAME,
    // Namespaced per organization — see the note at the top of this file.
    storage: createOrgScopedJSONStorage(PERSIST_NAME),
    version: 1,
      partialize: (state) => ({
        // persist only serializable state fields
        isFetching: state.isFetching,
        progress: state.progress,
        message: state.message,
        target: state.target,
        modalClosedByUser: state.modalClosedByUser,
        retrievalCompleted: state.retrievalCompleted,
        currentRetrievalId: state.currentRetrievalId,
        selectedRetrievalId: state.selectedRetrievalId,
        // exclude subscriptionObserver (not serializable)
      }),

      // Reset isFetching on rehydration
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Reset isFetching to false on store hydration
          state.isFetching = false;
        }
      },
}
));

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Unsubscribe from the outgoing organization's retrieval channel, blank the
// in-memory progress, then rehydrate the incoming organization's own document.
// Leaving the Phoenix channel attached would keep pushing Org A's percentages
// into a store now being read as Org B's.
registerOrgScopedState({
  name: "metadataProgressStore",
  onOrgChange: () => {
    useMetadataProgressStore.getState().cleanup();
    useMetadataProgressStore.setState({
      isFetching: false,
      progress: 0,
      message: "",
      target: null,
      modalClosedByUser: false,
      retrievalCompleted: false,
      subscriptionObserver: null,
      currentRetrievalId: null,
      selectedRetrievalId: null,
    });
    void useMetadataProgressStore.persist.rehydrate();
  },
});
